'use client';

import { useAccount, useSignTypedData } from 'wagmi';
import { useState, useCallback } from 'react';
import { ActionBuilder } from '@/api/hyperliquid/actions/ActionBuilder';
import { RequestSender } from '@/api/hyperliquid/client/RequestSender';
import type {
  ApproveAgentAction,
  UsdSendAction,
  SpotSendAction,
  WithdrawAction,
  UsdClassTransferAction,
  DefaultResponse,
  ApproveBuilderFeeAction,
} from '@/api/hyperliquid/types/exchange';
import { defaultChain } from '@/providers/Providers';
import { Address, parseSignature, verifyTypedData } from 'viem';

/**
 * Hook for Hyperliquid actions that require main wallet signing
 * Used for deposits, withdrawals, and agent approvals
 */
export function useHyperliquidMain() {
  const { address } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const actionBuilder = new ActionBuilder();

  const baseURL = process.env.NEXT_PUBLIC_HYPERLIQUID_API_ENDPOINT;

  const requestSender = new RequestSender({ baseURL });

  /**
   * Sign a user action using the connected wallet
   */
  const signUserAction = useCallback(
    async (
      action:
        | ApproveAgentAction
        | UsdSendAction
        | SpotSendAction
        | WithdrawAction
        | UsdClassTransferAction
        | ApproveBuilderFeeAction,
    ) => {
      if (!address) throw new Error('No wallet connected');

      // Create EIP-712 typed data for the action
      const domain = {
        name: 'HyperliquidSignTransaction',
        version: '1',
        chainId: defaultChain.id,
        verifyingContract: '0x0000000000000000000000000000000000000000' as Address,
      };

      // Map action type to typed data
      const getTypedData = () => {
        switch (action.type) {
          case 'approveAgent':
            return {
              types: {
                'HyperliquidTransaction:ApproveAgent': [
                  { name: 'hyperliquidChain', type: 'string' },
                  { name: 'agentAddress', type: 'address' }, // address 타입으로 변경
                  { name: 'agentName', type: 'string' },
                  { name: 'nonce', type: 'uint64' },
                ],
              },
              primaryType: 'HyperliquidTransaction:ApproveAgent' as const,
              message: {
                hyperliquidChain: action.hyperliquidChain,
                agentAddress: action.agentAddress as Address, // address 형식으로 변환
                agentName: action.agentName || '',
                nonce: BigInt(action.nonce),
              },
            };

          case 'usdSend':
            return {
              types: {
                'HyperliquidTransaction:UsdSend': [
                  { name: 'hyperliquidChain', type: 'string' },
                  { name: 'destination', type: 'string' },
                  { name: 'amount', type: 'string' },
                  { name: 'time', type: 'uint64' },
                ],
              },
              primaryType: 'HyperliquidTransaction:UsdSend' as const,
              message: {
                hyperliquidChain: action.hyperliquidChain,
                destination: action.destination,
                amount: action.amount,
                time: BigInt(action.time),
              },
            };

          case 'spotSend':
            return {
              types: {
                'HyperliquidTransaction:SpotSend': [
                  { name: 'hyperliquidChain', type: 'string' },
                  { name: 'destination', type: 'string' },
                  { name: 'token', type: 'string' },
                  { name: 'amount', type: 'string' },
                  { name: 'time', type: 'uint64' },
                ],
              },
              primaryType: 'HyperliquidTransaction:SpotSend' as const,
              message: {
                hyperliquidChain: action.hyperliquidChain,
                destination: action.destination,
                token: action.token,
                amount: action.amount,
                time: BigInt(action.time),
              },
            };

          case 'withdraw3':
            return {
              types: {
                'HyperliquidTransaction:Withdraw': [
                  { name: 'hyperliquidChain', type: 'string' },
                  { name: 'destination', type: 'string' },
                  { name: 'amount', type: 'string' },
                  { name: 'time', type: 'uint64' },
                ],
              },
              primaryType: 'HyperliquidTransaction:Withdraw' as const,
              message: {
                hyperliquidChain: action.hyperliquidChain,
                destination: action.destination,
                amount: action.amount,
                time: BigInt(action.time),
              },
            };

          case 'usdClassTransfer':
            return {
              types: {
                'HyperliquidTransaction:UsdClassTransfer': [
                  { name: 'hyperliquidChain', type: 'string' },
                  { name: 'amount', type: 'string' },
                  { name: 'toPerp', type: 'bool' },
                  { name: 'nonce', type: 'uint64' },
                ],
              },
              primaryType: 'HyperliquidTransaction:UsdClassTransfer' as const,
              message: {
                hyperliquidChain: action.hyperliquidChain,
                amount: action.amount,
                toPerp: action.toPerp,
                nonce: BigInt(action.nonce),
              },
            };

          case 'approveBuilderFee':
            return {
              types: {
                'HyperliquidTransaction:ApproveBuilderFee': [
                  { name: 'hyperliquidChain', type: 'string' },
                  { name: 'maxFeeRate', type: 'string' },
                  { name: 'builder', type: 'address' },
                  { name: 'nonce', type: 'uint64' },
                ],
              },
              primaryType: 'HyperliquidTransaction:ApproveBuilderFee' as const,
              message: {
                hyperliquidChain: action.hyperliquidChain,
                maxFeeRate: action.maxFeeRate,
                builder: action.builder as Address,
                nonce: BigInt(action.nonce),
              },
            };

          default:
            throw new Error(`Unsupported action type: ${action.type}`);
        }
      };

      const typedData = getTypedData();
      const signature = await signTypedDataAsync({
        domain,
        types: typedData.types,
        primaryType: typedData.primaryType,
        message: typedData.message,
      });

      // Parse signature to r, s, v format
      const sig = signature.slice(2);
      const r = '0x' + sig.slice(0, 64);
      const s = '0x' + sig.slice(64, 128);
      const v = parseInt(sig.slice(128, 130), 16);

      const verifyTypedDataResult = verifyTypedData({
        domain,
        types: typedData.types,
        primaryType: typedData.primaryType,
        message: typedData.message,
        signature,
        address,
      });

      return { r, s, v };
    },
    [address, signTypedDataAsync],
  );

  /**
   * Approve an agent/API wallet
   */
  const approveAgent = useCallback(
    async (agentAddress: string, agentName?: string): Promise<DefaultResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const action = actionBuilder.approveAgentAction(agentAddress, agentName);
        const signature = await signUserAction(action);
        const nonce = action.nonce;

        const result = await requestSender.sendExchangeRequest<DefaultResponse>(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [actionBuilder, signUserAction, requestSender],
  );

  /**
   * Send USDC
   */
  const sendUsdc = useCallback(
    async (destination: string, amount: string): Promise<DefaultResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const action = actionBuilder.usdSendAction(destination, amount);
        const signature = await signUserAction(action);
        const nonce = action.time;

        const result = await requestSender.sendExchangeRequest<DefaultResponse>(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [actionBuilder, signUserAction, requestSender],
  );

  /**
   * Send Spot tokens
   */
  const sendSpot = useCallback(
    async (destination: string, token: string, amount: string): Promise<DefaultResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const action = actionBuilder.spotSendAction(destination, token, amount);
        const signature = await signUserAction(action);
        const nonce = action.time;

        const result = await requestSender.sendExchangeRequest<DefaultResponse>(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [actionBuilder, signUserAction, requestSender],
  );

  /**
   * Withdraw to L1
   */
  const withdraw = useCallback(
    async (destination: string, amount: string): Promise<DefaultResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const action = actionBuilder.withdrawAction(destination, amount);
        const signature = await signUserAction(action);
        const nonce = action.time;

        const result = await requestSender.sendExchangeRequest<DefaultResponse>(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [actionBuilder, signUserAction, requestSender],
  );

  /**
   * Transfer between Spot and Perp
   */
  const transferUsdClass = useCallback(
    async (amount: string, toPerp: boolean, subaccount?: string): Promise<DefaultResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const action = actionBuilder.usdClassTransferAction(amount, toPerp, subaccount);
        const signature = await signUserAction(action);
        const nonce = action.nonce;

        const result = await requestSender.sendExchangeRequest<DefaultResponse>(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [actionBuilder, signUserAction, requestSender],
  );

  /**
   * Generic sign and send for custom actions
   */
  const signAndSendAction = useCallback(
    async (action: any): Promise<any> => {
      setIsLoading(true);
      setError(null);

      try {
        const signature = await signUserAction(action);
        const nonce = action.nonce || action.time || Date.now();

        const result = await requestSender.sendExchangeRequest(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [signUserAction, requestSender],
  );

  /**
   * Approve builder fee for an address
   */
  const approveBuilderFee = useCallback(
    async (builder: string, maxFeeRate: string): Promise<DefaultResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const nonce = Date.now();
        const action = actionBuilder.approveBuilderFeeAction(builder, maxFeeRate, nonce);
        const signature = await signUserAction(action);
        const result = await requestSender.sendExchangeRequest<DefaultResponse>(action, signature, nonce);

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [signUserAction, requestSender],
  );

  /**
   * Create Fandom with ERC-721 signature - Raw backend call
   */
  const createFandom = useCallback(
    async (fandomName: string, description: string, imageUrl: string) => {
      if (!address) throw new Error('No wallet connected');

      setIsLoading(true);
      setError(null);

      try {
        // Create EIP-712 typed data for fandom creation (ERC-721 style)
        const domain = {
          name: 'KPerpFandom',
          version: '1',
          chainId: defaultChain.id,
          verifyingContract: '0x0000000000000000000000000000000000000721' as Address,
        };

        const types = {
          CreateFandom: [
            { name: 'creator', type: 'address' },
            { name: 'fandomName', type: 'string' },
            { name: 'description', type: 'string' },
            { name: 'imageUrl', type: 'string' },
            { name: 'creationFee', type: 'uint256' },
            { name: 'timestamp', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
          ],
        };

        const nonce = Date.now();
        const message = {
          creator: address,
          fandomName,
          description,
          imageUrl,
          creationFee: BigInt(100 * 10 ** 6), // 100 USDC (6 decimals)
          timestamp: BigInt(Math.floor(Date.now() / 1000)),
          nonce: BigInt(nonce),
        };

        // Sign the typed data
        const signature = await signTypedDataAsync({
          domain,
          types,
          primaryType: 'CreateFandom' as const,
          message,
        });

        // Parse signature
        const sig = signature.slice(2);
        const r = '0x' + sig.slice(0, 64);
        const s = '0x' + sig.slice(64, 128);
        const v = parseInt(sig.slice(128, 130), 16);

        // Direct backend call with raw fetch to external server
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT || 'https://d3fdq9a70gh060.cloudfront.net/api';

        const requestBody = {
          v: v,
          r: r,
          s: s,
          message: {
            creator: address,
            fandomName,
            description,
            imageUrl,
            creationFee: '100000000', // 100 USDC in smallest unit
            timestamp: Math.floor(Date.now() / 1000),
            nonce,
          },
          signerAddress: address,
          image: imageUrl, // Add image field for backend
        };

        const response = await fetch(`${backendUrl}/create-fandom`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to create fandom');
        }

        return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [address, signTypedDataAsync],
  );

  return {
    // Methods
    approveAgent,
    sendUsdc,
    sendSpot,
    withdraw,
    transferUsdClass,
    signAndSendAction,
    approveBuilderFee,
    createFandom,

    // State
    isLoading,
    error,

    // Utils
    actionBuilder,
    requestSender,
  };
}
